import React from "react";
import { useSelector } from "react-redux";
import { Navigate } from "react-router-dom";

// import type { RootState } from "../../store/state/rootReducer";

import { useRoleFlags } from "../../hooks/usePermissions";

import { selectAuthenticated } from "./authSelectors";

// Authenication Higher Order Component
// if the authenticated, it proceeds. If not, auto routes to welcome page
// 'auth_level' property specifies
//    - "guest"   - anyone who was issued an authentication token (note: when guest users
//                  first join a call, or do solo recording, the system will also issue
//                  them a token so they can do certain actions with the platform)
//    - "user"    - any logged on user
//    - "manager" - manager of organization (or admin who is by default manager for all)
//    - "org_manager" - manager of organization (UPDATE description)
//    - "admin"   - global site admin
// If 'auth_level' is not provided, default access level is 'user'.

export function RequireAuth({
  children,
  authLevel = "user",
}: {
  children: JSX.Element;
  authLevel?: "guest" | "user" | "manager" | "org_manager" | "admin";
}) {
  const PUBLIC_SITE_URL = process.env.PUBLIC_SITE_URL;
  const authenticated = useSelector(selectAuthenticated);
  const { isAdmin, isOrgManager, isProjectManager } = useRoleFlags();

  // Mapping authLevel to CASL capabilities
  const isAuthorized = () => {
    switch (authLevel) {
      case "admin":
        return isAdmin;
      case "org_manager":
        return isOrgManager;
      case "manager":
        return isProjectManager || isOrgManager || isAdmin;
      case "user":
        return true; // useRoleFlags logic already ensures ability is present
      case "guest":
        return true;
      default:
        return false;
    }
  };

  if (!authenticated) {
    window.location.href = PUBLIC_SITE_URL;
    return null;
  }

  if (!isAuthorized()) {
    return <Navigate to="/" />;
  }

  return children;
}
